import { Segmented } from 'antd';

import { Select } from '../../../components/ui';
import type { FieldControlProps } from '../../../components/ui/Field';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { useNhanThoiHan } from '../../tenant/components/nhan-thoi-han';
import type { AiTokenDurationUnit, AiTokenTopUpSnapshot } from '../../tenant/types';
import { dinhDangUsd, THANG_HOP_LE, TUAN_HOP_LE } from '../nghiep-vu/fox';

/** `50.00` → "50,00 USD" theo ngôn ngữ; `null` → "—". */
export function useTienUsd() {
  const { t, locale } = useT();
  return (usd: string | null | undefined) => (usd ? t('banToken.chung.usd', { so: dinhDangUsd(usd, locale) }) : KHONG_CO);
}

/** `1300000` → "≈ 1.300.000 ₫"; `null` → `null` (không hiện dòng). */
export function useTienVnd() {
  const { t, locale } = useT();
  return (vnd: string | null | undefined) => (vnd ? t('banToken.chung.xapXiVnd', { so: soVN(Number(vnd), locale) }) : null);
}

/** Giá USD và dòng phụ ≈ VND, căn phải trong ô bảng. */
export function OGia({ usd, vnd, phu }: { usd: string | null; vnd: string | null; phu?: string }) {
  const tienUsd = useTienUsd();
  const tienVnd = useTienVnd();
  const xapXi = tienVnd(vnd);
  return (
    <div className="flex flex-col items-end gap-0.5 whitespace-nowrap">
      <span className="text-sm tabular-nums text-content-primary">{tienUsd(usd)}</span>
      {(xapXi || phu) && (
        <span className="text-2xs tabular-nums text-content-muted">{[phu, xapXi].filter(Boolean).join(' · ')}</span>
      )}
    </div>
  );
}

/**
 * Ảnh chụp giá của một yêu cầu nạp token hay túi sinh từ nó (ADR-0100 mục 5): nguồn (gói hoặc số tiền), giá USD, ≈ VND
 * kèm tỷ giá và ngày. Không có ảnh chụp thì "—".
 */
export function ChupGia({ chup, gon }: { chup: AiTokenTopUpSnapshot | null | undefined; gon?: boolean }) {
  const { t, locale } = useT();
  const tienUsd = useTienUsd();
  const tienVnd = useTienVnd();
  if (!chup) return <span className="text-xs text-content-muted">{KHONG_CO}</span>;
  const nguon = chup.package_name ? t('banToken.chup.goi', { ten: chup.package_name }) : t('banToken.chup.theoTien');
  const xapXi = tienVnd(chup.price_vnd);
  const tyGia =
    chup.exchange_rate && chup.exchange_rate_date
      ? t('banToken.chup.tyGia', { so: soVN(Number(chup.exchange_rate), locale), ngay: ngayVN(chup.exchange_rate_date) })
      : null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs tabular-nums text-content-primary">
        {nguon} · {tienUsd(chup.price_usd)}
        {xapXi ? ` ${xapXi}` : ''}
      </span>
      {!gon && tyGia && <span className="text-2xs tabular-nums text-content-muted">{tyGia}</span>}
    </div>
  );
}

/** Thời hạn của gói nạp token: đơn vị tuần hoặc tháng, số trong 1–4 tuần hoặc 1, 3, 6, 12 tháng. */
export function ChonThoiHanNap({
  p,
  donVi,
  soLuong,
  onChange,
}: {
  p: FieldControlProps;
  donVi: AiTokenDurationUnit;
  soLuong: number;
  onChange: (donVi: AiTokenDurationUnit, soLuong: number) => void;
}) {
  const { t } = useT();
  const nhan = useNhanThoiHan();
  const cacSo: readonly number[] = donVi === 'WEEK' ? TUAN_HOP_LE : THANG_HOP_LE;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented<AiTokenDurationUnit>
        aria-label={t('tokenAi.thDonVi')}
        options={[
          { value: 'MONTH', label: t('tokenAi.thDonViThang') },
          { value: 'WEEK', label: t('tokenAi.thDonViTuan') },
        ]}
        value={donVi}
        onChange={(u) => onChange(u, 1)}
      />
      <Select<number>
        {...p}
        className="w-32"
        value={soLuong}
        onChange={(n) => onChange(donVi, n)}
        options={cacSo.map((n) => ({ value: n, label: nhan(donVi, n) }))}
      />
    </div>
  );
}
